/** @jsx jsx */

import { Fragment as _, useState, useRef } from 'react';
import { css, jsx, keyframes } from '@emotion/core';
import { HotSpotTickedWhenSeen } from './HotSpotTickedWhenSeen';

const outerPulse = (x, y) => keyframes`
0% {
  // opacity: .5;
  opacity: 1;
  transform: scale(0);
  transform-origin: ${x}px ${y}px;
}
// 50% {
//   opacity: 0.8;
// }
80%, 100% {
  opacity: 0;
  transform: scale(1);
  transform-origin: ${x}px ${y}px;
}
`;

const innerPulse = (x, y) => keyframes`
0% {
  transform: scale(.8);
  transform-origin: ${x}px ${y}px;
}
50% {
  transform: scale(1);
  transform-origin: ${x}px ${y}px;
}
100% {
  transform: scale(.8);
  transform-origin: ${x}px ${y}px;
}
`;

const focus = keyframes`
0% {
  transform: scale(0);
}
100% {
  transform: scale(1);
}
`;

const blur = keyframes`
0% {
  transform: scale(1);
}
100% {
  transform: scale(0);
}`;

const VisibleHotSpot = ({
  id,
  // size = 50,
  location,
  color = 'rgba(255, 0, 99, 0.5)',
  hotSpotsAnimated,
  hotSpotsVisible,
  inverseScale,
  hasBeenSeen,
  showSeenState,
  // showCorrectAnswer,
  submitState,
  hotSpotFocus,
  focusStrokeWidth = 2,
  immediateFeedback = true,
  isCorrect = false,
  enabled = true,
  showChecked,
  onClick,
  ...props
}: {
  id: string;
  // size?: number;
  location: number[];
  color?: string;
  hotSpotsAnimated?: boolean;
  hotSpotsVisible?: boolean;
  creating?: boolean;
  hasBeenSeen?: boolean;
  showSeenState?: boolean | 0 | 1;
  showCorrectAnswer?: boolean;
  inverseScale: number;
  hotSpotFocus: string | undefined;
  focusStrokeWidth?: number;
  immediateFeedback?: boolean;
  submitState: string;
  isCorrect?: boolean;
  enabled?: boolean;
  showChecked?: boolean | undefined;
  onClick: (e: any) => void;
}) => {
  //

  // updates onResize since parent does for calculating scaleFactor
  let size = (props as any).size || 50;
  if (window.innerWidth < 480) {
    size = size * 0.6;
  }

  const [hover, setHover] = useState(false);
  // bit of a dance here to make sure if something ' goes wrong ' and we have an array
  // of more than 2 entries ( nested array )
  const [x, y] = location;

  const visibility = window.dev() ? true : hotSpotsVisible;
  //

  const radii = useRef<{ outer: number; inner: number }>({
    outer: 0,
    inner: 0,
  });

  if (![0, Infinity].includes(size / 2)) {
    radii.current.outer = size / 2;
  }

  if (![0, Infinity].includes(size / 2 / 3)) {
    radii.current.inner = size / 2 / 3;
  }

  return (
    <g
      style={{
        pointerEvents: !immediateFeedback
          ? enabled
            ? 'auto'
            : 'none'
          : 'auto',
      }}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      onClick={onClick}
    >
      <circle
        id={id}
        className="fader_-2s"
        cx={x}
        cy={y}
        r={radii.current.outer} // size / 2
        css={css`
          animation: ${!window.dev() && !hasBeenSeen && hotSpotsAnimated
              ? !hover
                ? outerPulse(x, y)
                : ''
              : ''}
            1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
        `}
        style={{
          fill: visibility ? color : 'transparent',
          ...(window.dev && window.dev()
            ? {
                cursor: 'pointer',
                opacity: hover ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }
            : {
                cursor: 'pointer',
              }),

          transition: 'fill 0.1s',
        }}
      />

      {hotSpotsAnimated && (
        <circle
          cx={x}
          cy={y}
          r={radii.current.inner} // size / 2 / 3
          css={css`
            pointer-events: none;
            animation: ${!window.dev() ? (!hover ? innerPulse(x, y) : '') : ''}
              1.25s cubic-bezier(0.455, 0.03, 0.515, 0.955) -0.4s infinite;
          `}
          style={{
            fill: visibility ? color : 'transparent',
            transition: 'fill 0.2s',
          }}
        />
      )}

      <svg x={x} y={y} overflow="visible">
        {`hotspot-${hotSpotFocus}` === id && (
          <circle
            cx={0}
            cy={0}
            r={size / 1.8}
            css={css({
              pointerEvents: 'none',
              fill: 'transparent',
              stroke: color,
              strokeWidth: 2 * inverseScale,
            })}
          />
        )}
      </svg>

      {showChecked && (
        <HotSpotTickedWhenSeen
          x={x}
          y={y}
          size={size}
          color={color}
          inverseScale={inverseScale}
          displayAsRadio={submitState && submitState !== 'resolved'}
        />
      )}
    </g>
  );
};

export default VisibleHotSpot;
